<template>
  <div v-resize="onResize" ref="container" class="caption">
    <AppChartLegend :items="legendItems" />
    <div class="sankey-container">
      <div
        :style="{
          width: `${sideBarWidth}px`,
          height: `${height + 2 * outerPadding}px`
        }"
        class="sankey-side-bar font-weight-bold"
      >
        <div
          v-for="key in Object.keys(sankeyInfo)"
          :key="key"
          :style="{ 'text-align': $vuetify.rtl ? 'left' : 'right' }"
        >
          {{ $t(`resolutionPath.${key}.intro`) }}
        </div>
      </div>
      <svg v-if="hasData" :width="width" :height="height + 2 * outerPadding">
        <g :transform="`translate(${width},${outerPadding})rotate(90)`">
          <g class="links" fill="none">
            <path
              v-for="link in sankeyData.links"
              :key="link.key"
              :d="sankeyLinkHorizontal(link)"
              :stroke-width="link.width"
              :stroke="pathColor(link)"
              class="sankey-path"
            ></path>
          </g>
          <g class="nodes">
            <rect
              v-for="(node, i) in sankeyData.nodes"
              :key="i"
              :x="node.x0"
              :y="node.y0"
              :height="node.y1 - node.y0"
              :width="node.x1 - node.x0"
            ></rect>
          </g>
          <g class="labels">
            <text
              v-for="(node, i) in sankeyData.nodes"
              :key="`${i}-bg`"
              :x="node.x0 + 10"
              :y="(node.y1 + node.y0 - 15) / 2"
              dy="0.35em"
              :text-anchor="$vuetify.rtl ? 'end' : 'start'"
              :transform="`rotate(215,${node.x1},${(node.y1 + node.y0) / 2})`"
              class="label-background"
            >
              {{ $t(`resolutionPath.${node.variable}.labels.${node.name}`) }}
            </text>
            <text
              v-for="(node, i) in sankeyData.nodes"
              :key="i"
              :x="node.x0 + 10"
              :y="(node.y1 + node.y0 - 15) / 2"
              dy="0.35em"
              :text-anchor="$vuetify.rtl ? 'end' : 'start'"
              :transform="`rotate(215,${node.x1},${(node.y1 + node.y0) / 2})`"
            >
              {{ $t(`resolutionPath.${node.variable}.labels.${node.name}`) }}
            </text>
            <text
              v-for="(node, ii) in sankeyData.nodes"
              :key="`${ii}-value-bg`"
              :x="node.x1"
              :y="(node.y1 + node.y0) / 2"
              dy="1em"
              text-anchor="middle"
              class="label-background"
              :transform="`rotate(-90,${node.x1},${(node.y1 + node.y0) / 2})`"
            >
              {{ node.formated }}
            </text>
            <text
              v-for="(node, ii) in sankeyData.nodes"
              :key="`${ii}-value`"
              :x="node.x1"
              :y="(node.y1 + node.y0) / 2"
              dy="1em"
              text-anchor="middle"
              :transform="`rotate(-90,${node.x1},${(node.y1 + node.y0) / 2})`"
            >
              {{ node.formated }}
            </text>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import AppChartLegend from "@/components/AppChartLegend.vue";
import { mapGetters } from "vuex";
import { format } from "d3-format";
import { ascending } from "d3-array";
import { scaleOrdinal } from "d3-scale";
import { quantize, interpolateHcl } from "d3-interpolate";
import { rollup, sum } from "d3-array";
import * as d3Sankey from "d3-sankey";
import info from "@/locales/en.json";

export default {
  components: {
    AppChartLegend
  },
  data() {
    return {
      sankeyInfo: info.resolutionPath,
      sankeyLegendInfo: info.resolutionPathLegend,
      width: 500,
      height: 600,
      sideBarWidth: 100,
      nodeWidth: 4,
      nodePadding: 16,
      outerPadding: 36
    };
  },
  computed: {
    ...mapGetters([
      "filteredCount",
      "filteredData",
      "filteredSankeyResolutonPathData"
    ]),
    filteredSankeyResolutonPath() {
      return this.filteredSankeyResolutonPathData();
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    hasData() {
      return this.filteredSankeyResolutonPath.length > 0;
    },
    graph() {
      if (!this.hasData) return;
      const variables = Object.keys(this.sankeyInfo);

      const indexByKey = new Map();
      const linkByKey = new Map();

      const allNodes = [];
      variables.forEach(variable => {
        let names = Object.keys(this.sankeyInfo[variable].labels);
        if (!this.$vuetify.rtl) {
          names = names.reverse();
        }
        names.forEach(name => {
          allNodes.push({ variable, name });
        });
      });

      for (const node of allNodes) {
        node.key = JSON.stringify([node.variable, node.name]);
      }

      const usedNodeKeys = new Set();
      for (const d of this.filteredSankeyResolutonPath) {
        for (const variable of variables) {
          if (d[variable]) {
            usedNodeKeys.add(JSON.stringify([variable, d[variable]]));
          }
        }
      }
      const nodes = allNodes.filter(node => usedNodeKeys.has(node.key));

      let index = -1;
      for (const node of nodes) {
        indexByKey.set(node.key, ++index);
      }

      const links = [];
      function addLinks(prefix, data) {
        for (const d of data) {
          const names = variables
            .filter(
              (variable, i) => d[variable] && i <= variables.indexOf(prefix[1])
            )
            .map(variable => d[variable]);
          const key = JSON.stringify(names);
          const value = d.value;
          let link = linkByKey.get(key);
          if (link) {
            link.value += value;
            continue;
          }
          const source = indexByKey.get(
            JSON.stringify([prefix[0], d[prefix[0]]])
          );
          const target = indexByKey.get(
            JSON.stringify([prefix[1], d[prefix[1]]])
          );
          link = {
            source,
            target,
            names,
            value,
            key
          };
          links.push(link);
          linkByKey.set(key, link);
        }
      }

      addLinks(
        ["1", "2"],
        this.filteredSankeyResolutonPath.filter(d => d["2"])
      );
      addLinks(
        ["2", "3"],
        this.filteredSankeyResolutonPath.filter(d => d["2"])
      );
      addLinks(
        ["1", "3"],
        this.filteredSankeyResolutonPath.filter(d => !d["2"])
      );

      links.sort(
        (a, b) => ascending(a.target, b.target) || ascending(a.source, b.source)
      );
      return { nodes, links };
    },
    sankey() {
      return d3Sankey
        .sankey()
        .nodeSort(null)
        .linkSort(null)
        .nodeWidth(this.nodeWidth)
        .nodePadding(this.nodePadding)
        .extent([
          [0, this.nodePadding],
          [this.height, this.width - this.nodePadding]
        ]);
    },
    sankeyData() {
      if (!this.hasData) return;
      const sankeyData = this.sankey({
        nodes: this.graph.nodes.map(d => Object.assign({}, d)),
        links: this.graph.links.map(d => Object.assign({}, d))
      });
      const stageTotal = rollup(
        sankeyData.nodes,
        v => sum(v, d => d.value),
        d => d.variable
      );
      sankeyData.nodes.forEach(d => {
        d.formated = format(".0%")(d.value / stageTotal.get(d.variable));
      });
      return sankeyData;
    },
    pathColorScale() {
      const strategies = Object.keys(this.sankeyInfo["2"].labels);
      const unknown = this.$vuetify.theme.themes.light.secondary;
      const colors = ["#194173", "#A7C7AD", "#5B2A7E", "#7FBED1"];
      return scaleOrdinal()
        .domain(strategies)
        .range(colors)
        .unknown(unknown);
    },
    legendItems() {
      const colors = this.pathColorScale
        .range()
        .concat(this.pathColorScale.unknown());
      return Object.keys(this.sankeyLegendInfo).map((key, i) => ({
        label: this.$t(`resolutionPathLegend.${key}`),
        color: colors[i],
        shape: "square"
      }));
    }
  },
  watch: {
    breakpoint() {
      this.onResize();
    }
  },
  methods: {
    onResize() {
      this.width = this.$refs.container.clientWidth - this.sideBarWidth;
    },
    sankeyLinkHorizontal(link) {
      return d3Sankey.sankeyLinkHorizontal()(link);
    },
    pathColor(link) {
      const name = link.names[0] === "2" ? "" : link.names[1];
      return this.pathColorScale(name);
    }
  }
};
</script>

<style scoped>
.sankey-path {
  mix-blend-mode: multiply;
}

.sankey-container {
  display: flex;
}

.sankey-side-bar {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 10px;
  padding-top: 10px;
  line-height: 16px;
}

.label-background {
  stroke-width: 3px;
  stroke: #fff;
}

.labels {
  pointer-events: none;
}
</style>